import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, RoleRoute } from "@/components/ProtectedRoute";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import Home from "@/pages/Home";
import { AllyWidget } from "@/components/AllyWidget";
import { PublicAlly } from "@/components/PublicAlly";

const Insights = lazy(() => import("@/pages/Insights"));
const InsightPost = lazy(() => import("@/pages/InsightPost"));
const Refer = lazy(() => import("@/pages/Refer"));
const Contact = lazy(() => import("@/pages/Contact"));
const RequestAccess = lazy(() => import("@/pages/RequestAccess"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Login = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.Register })));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ExchangeDetail = lazy(() => import("@/pages/ExchangeDetail"));
const Rules = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Rules })));
const Terms = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Privacy })));
const RefundPolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.RefundPolicy })));
const About = lazy(() => import("@/pages/About"));
const SmsOptIn = lazy(() => import("@/pages/SmsOptIn"));
const FirmDashboard = lazy(() => import("@/pages/enterprise/FirmDashboard"));
const Clients = lazy(() => import("@/pages/enterprise/Clients"));
const ClientDetail = lazy(() => import("@/pages/enterprise/ClientDetail"));
const FirmExchangeDetail = lazy(() => import("@/pages/enterprise/FirmExchangeDetail"));
const Compliance = lazy(() => import("@/pages/enterprise/Compliance"));
const EnterpriseSettings = lazy(() => import("@/pages/enterprise/EnterpriseSettings"));
const AdminPanel = lazy(() => import("@/pages/admin/AdminPanel"));
const MarketPulseAdmin = lazy(() => import("@/pages/admin/MarketPulseAdmin"));
const EnterpriseLogin = lazy(() => import("@/pages/EnterpriseLogin"));
const TopExchangers = lazy(() => import("@/pages/enterprise/TopExchangers"));
const BrandedLogin = lazy(() => import("@/pages/BrandedLogin"));
const ClientLogin = lazy(() => import("@/pages/client/ClientLogin"));
const ClientMagic = lazy(() => import("@/pages/client/ClientMagic"));
const ClientPortal = lazy(() => import("@/pages/client/ClientPortal"));
const ClientExchange = lazy(() => import("@/pages/client/ClientExchange"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const StateTaxIndex = lazy(() => import("@/pages/StateTaxIndex"));
const StateTaxDetail = lazy(() => import("@/pages/StateTaxDetail"));
const ExchangeCompanies = lazy(() => import("@/pages/ExchangeCompanies"));
const SeoLanding = lazy(() => import("@/pages/SeoLanding"));
import { SEO_PAGES } from "@/data/seoPages";
const SignDocument = lazy(() => import("@/pages/SignDocument"));
const SharedExchange = lazy(() => import("@/pages/SharedExchange"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const StartCheckout = lazy(() => import("@/pages/StartCheckout"));
const MarketPulse = lazy(() => import("@/pages/MarketPulse"));
const NDA = lazy(() => import("@/pages/NDA"));
const FirmMeetings = lazy(() => import("@/pages/FirmMeetings"));
const VideoCall = lazy(() => import("@/pages/VideoCall"));
const TrustCenter = lazy(() => import("@/pages/TrustCenter"));
const ForBanks = lazy(() => import("@/pages/ForBanks"));
const Watch = lazy(() => import("@/pages/Watch"));
const NistCsf = lazy(() => import("@/pages/NistCsf"));
const SecurityQuestionnaire = lazy(() => import("@/pages/SecurityQuestionnaire"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <Loader2 className="animate-spin text-ally-pink" size={36} />
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash, search } = useLocation();
  useEffect(() => {
    const code = new URLSearchParams(search).get("ref");
    if (code) localStorage.setItem("deferally_ref", code.toUpperCase());
  }, [search]);
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else if (tries++ < 15) setTimeout(tryScroll, 100);
      };
      tryScroll();
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/start" element={<StartCheckout />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightPost />} />
            <Route path="/refer" element={<Refer />} />
            <Route path="/pulse" element={<MarketPulse />} />
            <Route path="/nda" element={<NDA />} />
            <Route path="/trust" element={<TrustCenter />} />
            <Route path="/banks" element={<ForBanks />} />
            <Route path="/watch" element={<Watch />} />
            <Route path="/trust/nist-csf" element={<NistCsf />} />
            <Route path="/trust/security-questionnaire" element={<SecurityQuestionnaire />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/request-access" element={<RequestAccess />} />
            <Route path="/enterprise" element={<Navigate to="/" replace />} />
            <Route path="/guide" element={<Navigate to="/" replace />} />
            <Route path="/properties" element={<Navigate to="/" replace />} />
            <Route path="/savings/:slug" element={<Navigate to="/" replace />} />
            <Route path="/sign/:token" element={<SignDocument />} />
            <Route path="/shared/:token" element={<SharedExchange />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/state-taxes" element={<StateTaxIndex />} />
            <Route path="/state-taxes/:slug" element={<StateTaxDetail />} />
            <Route path="/1031-exchange-companies" element={<ExchangeCompanies />} />
            {SEO_PAGES.map((p) => <Route key={p.slug} path={`/${p.slug}`} element={<SeoLanding slug={p.slug} />} />)}
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/sms-opt-in" element={<SmsOptIn />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/enterprise/login" element={<EnterpriseLogin />} />
            <Route path="/ap" element={<BrandedLogin />} />
            <Route path="/portal/:slug" element={<BrandedLogin />} />
            <Route path="/client/login" element={<ClientLogin />} />
            <Route path="/client/magic/:token" element={<ClientMagic />} />
            <Route path="/client" element={<RoleRoute roles={["client"]} fallback="/client/login"><ClientPortal /></RoleRoute>} />
            <Route path="/client/exchanges/:id" element={<RoleRoute roles={["client"]} fallback="/client/login"><ClientExchange /></RoleRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/:id" element={<ProtectedRoute><ExchangeDetail /></ProtectedRoute>} />
            <Route path="/enterprise/app" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><FirmDashboard /></RoleRoute>} />
            <Route path="/enterprise/clients" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><Clients /></RoleRoute>} />
            <Route path="/enterprise/clients/:id" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><ClientDetail /></RoleRoute>} />
            <Route path="/enterprise/compliance" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><Compliance /></RoleRoute>} />
            <Route path="/enterprise/top-exchangers" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><TopExchangers /></RoleRoute>} />
            <Route path="/enterprise/settings" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><EnterpriseSettings /></RoleRoute>} />
            <Route path="/enterprise/meetings" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><FirmMeetings /></RoleRoute>} />
            <Route path="/enterprise/call/:id" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><VideoCall host={true} /></RoleRoute>} />
            <Route path="/client/call/:id" element={<RoleRoute roles={["client"]} fallback="/client/login"><VideoCall host={false} /></RoleRoute>} />
            <Route path="/enterprise/exchanges/:id" element={<RoleRoute roles={["enterprise", "superadmin", "support", "support_viewer"]}><FirmExchangeDetail /></RoleRoute>} />
            <Route path="/admin" element={<RoleRoute roles={["superadmin", "support", "support_viewer"]}><AdminPanel /></RoleRoute>} />
            <Route path="/admin/market-pulse" element={<RoleRoute roles={["superadmin", "support", "support_viewer"]}><MarketPulseAdmin /></RoleRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <AllyWidget />
          <PublicAlly />
          <ForcePasswordChange />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
